/**
 * OpportunitiesBackgroundSync
 *
 * Session-level warm for DeallensStore (DealLens list + thread tails), parity with
 * PropertyBackgroundSync. Prefetches when customer + viewer email are ready so
 * `/opportunities` and DealLens routes reuse cache instead of owning the first fetch.
 */

import { buildOpportunitiesLoadListFilters, DEFAULT_FILTERS } from "@/pages/Opportunities/opportunitiesDealHelpers";

const SYNC_INTERVAL_MS = 2 * 60 * 1000;
const MIN_SYNC_SPACING_MS = 10_000;

let _store = null;
let _activeCustomer = null;
let _activeUserEmail = null;
let _intervalId = null;
let _lastSyncSpacingAt = 0;

function _sessionKey() {
  return `${String(_activeCustomer ?? "")}|${String(_activeUserEmail ?? "")}`;
}

function _canSync() {
  return Boolean(
    _store?.opportunitiesStore &&
      _activeCustomer &&
      typeof _activeCustomer === "string" &&
      _activeUserEmail &&
      typeof _activeUserEmail === "string"
  );
}

function _sync(force = false) {
  const opportunitiesStore = _store?.opportunitiesStore;
  if (!_canSync()) return;

  const customer = _activeCustomer.trim();
  const userEmail = _activeUserEmail.trim();
  if (!customer || !userEmail) return;

  const t = Date.now();
  if (!force && t - _lastSyncSpacingAt < MIN_SYNC_SPACING_MS) return;
  _lastSyncSpacingAt = t;

  opportunitiesStore.loadListUntilComplete({
    customer,
    userEmail,
    /** ASSUMPTION (R6): Background sync intentionally uses `"receiver"`; see OpportunitiesHubStore. */
    queryRole: "receiver",
    nextPage: null,
    reset: false,
    revalidateAfterMs: opportunitiesStore.defaultListRevalidateAfterMs,
    filters: buildOpportunitiesLoadListFilters(DEFAULT_FILTERS),
  });
}

function runWhenVisible() {
  if (typeof document !== "undefined" && document.visibilityState === "visible" && _activeCustomer) {
    _sync(false);
  }
}

function start(store) {
  if (!store?.opportunitiesStore) return;
  _store = store;
  if (_intervalId != null) return;

  _intervalId = setInterval(runWhenVisible, SYNC_INTERVAL_MS);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", _onVisibilityChange);
    document.addEventListener("pagehide", _onPageHide);
  }
}

function _onPageHide() {
  if (_intervalId != null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}

function stop() {
  if (_intervalId != null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", _onVisibilityChange);
    document.removeEventListener("pagehide", _onPageHide);
  }
  _activeCustomer = null;
  _activeUserEmail = null;
  _store = null;
}

function _onVisibilityChange() {
  if (typeof document === "undefined") return;
  if (document.visibilityState === "hidden") {
    if (_intervalId != null) {
      clearInterval(_intervalId);
      _intervalId = null;
    }
    return;
  }
  if (document.visibilityState === "visible") {
    if (_intervalId == null) {
      _intervalId = setInterval(runWhenVisible, SYNC_INTERVAL_MS);
    }
    _sync(true);
  }
}

/**
 * @param {{ customer?: string | null, userEmail?: string | null }} session
 */
function setSession({ customer = null, userEmail = null } = {}) {
  const nextCustomer = customer == null || customer === "" ? null : String(customer).trim();
  const nextEmail = userEmail == null || userEmail === "" ? null : String(userEmail).trim();
  const isCustomerSwitch =
    Boolean(nextCustomer) && Boolean(_activeCustomer) && nextCustomer !== _activeCustomer;

  if (isCustomerSwitch && _store?.opportunitiesStore?.resetForNewCustomer) {
    _store.opportunitiesStore.resetForNewCustomer();
  }

  const prevKey = _sessionKey();
  _activeCustomer = nextCustomer;
  _activeUserEmail = nextEmail;
  const nextKey = _sessionKey();

  if (nextCustomer && nextEmail && (isCustomerSwitch || nextKey !== prevKey)) {
    _sync(true);
  }
}

/** @param {string | null | undefined} customerId */
function setActiveCustomer(customerId) {
  const email = String(_store?.authStore?.email ?? _activeUserEmail ?? "").trim();
  setSession({ customer: customerId, userEmail: email || null });
}

function runInitialSync() {
  _sync(true);
}

const OpportunitiesBackgroundSync = {
  start,
  stop,
  setSession,
  setActiveCustomer,
  runInitialSync,
};

export default OpportunitiesBackgroundSync;
